import React, { useState } from "react";
import { Instagram, Facebook, Youtube, X } from "lucide-react";
import NewsletterCapture from "./footer/NewsletterCapture";
import FooterLinkColumn from "./footer/FooterLinkColumn";
import { useAuth } from "../context/AuthContext";
import { KLAVIYO_ENABLED, openCookieSettings } from "../utils/klaviyo";
import { hasFlag, setFlag, SUBSCRIBED_FLAG, LEAD_DISMISSED_FLAG, HAS_ORDERED_FLAG } from "../utils/preferences";

const SHOP_LINKS = [
  { to: "/shop", label: "All flavours" },
  { to: "/shop", label: "Salt & Vinegar" },
  { to: "/shop", label: "Peanut Butter" },
  { to: "/shop", label: "Bundles" },
];

const COMPANY_LINKS = [
  { to: "/about", label: "Our story" },
  { to: "/story-behind-your-krunch", label: "Rajesh's story" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
  { to: "/contact", label: "Wholesale" },
];

const SUPPORT_LINKS = [
  { to: "/contact", label: "Delivery" },
  { to: "/contact", label: "Returns" },
  { to: "/faq", label: "Ingredients" },
  { to: "/faq", label: "Allergens" },
  { to: "/terms", label: "Terms & Conditions" },
];

const SOCIAL = [
  { key: "instagram", href: "https://instagram.com", Icon: Instagram, label: "Instagram" },
  { key: "facebook", href: "https://facebook.com", Icon: Facebook, label: "Facebook" },
  { key: "youtube", href: "https://youtube.com", Icon: Youtube, label: "YouTube" },
];

// The 10% offer is for newcomers: signed-in customers, subscribers and past buyers see a thank-you line
// instead, and anyone who closes the offer doesn't see it again.
function LeadCaptureSection() {
  const { isLoggedIn, user } = useAuth();
  // Read once per page load, so a fresh sign-up keeps seeing its discount code until they move on
  const [isMember] = useState(() => hasFlag(SUBSCRIBED_FLAG) || hasFlag(HAS_ORDERED_FLAG));
  const [isDismissed, setIsDismissed] = useState(() => hasFlag(LEAD_DISMISSED_FLAG));

  if (isLoggedIn || user !== null || isMember) {
    return (
      <div className="border-b border-cream/10 pb-10 mb-12 text-center" data-testid="footer-member-note">
        <p className="font-display text-cream/70 tracking-wide">You're part of the KrunchMate family!</p>
      </div>
    );
  }
  if (isDismissed) return null;

  const dismiss = () => {
    setFlag(LEAD_DISMISSED_FLAG);
    setIsDismissed(true);
  };

  return (
    <div className="relative border-b border-cream/10 pb-16 mb-12">
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-0 right-0 text-cream/40 hover:text-cream transition-colors"
        aria-label="Hide this offer"
        data-testid="footer-lead-dismiss"
      >
        <X size={18} />
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div>
          <div className="product-no mb-3">STAY KRUNCHY</div>
          <h3 className="h-section font-display text-cream leading-[1.05]">
            Get first dibs on new flavours &<br />
            <span className="font-beth text-[color:var(--gold)] normal-case">
              10% off your first pouch.
            </span>
          </h3>
        </div>
        <NewsletterCapture />
      </div>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="relative bg-[color:var(--pal-ink)] pt-20 pb-10 px-5 lg:px-10" data-testid="site-footer">
      <div className="container-x">
        <LeadCaptureSection />

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 pb-12">
          <div className="col-span-2">
            <img src="/images/logo-white.svg" alt="KrunchMate" className="w-36 mb-6" />
            <p className="text-cream/60 max-w-xs leading-relaxed text-sm">
              Small-batch popped water-lily seed snacks. Sourced in India, packed in the UK.
              Cinematically krunchy.
            </p>
            <div className="flex items-center gap-4 mt-6 text-cream/70">
              {SOCIAL.map(({ key, href, Icon, label }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="hover:text-gold transition-colors"
                  data-testid={`social-${key}`}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>
          <FooterLinkColumn title="Shop" links={SHOP_LINKS} />
          <FooterLinkColumn title="Company" links={COMPANY_LINKS} />
          <FooterLinkColumn title="Support" links={SUPPORT_LINKS} />
        </div>

        <div className="pt-6 border-t border-cream/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-cream/45 text-xs">
          <div>© {new Date().getFullYear()} KrunchMate Ltd. Sourced in India, packed in the UK.</div>
          <div className="font-rusty text-[color:var(--gold)] text-lg">Snack Smarter, Krunch Now!</div>
          <div className="flex items-center gap-4">
            <span>Registered in England & Wales.</span>
            {KLAVIYO_ENABLED && (
              <button type="button" onClick={openCookieSettings} className="underline hover:text-cream transition-colors">
                Cookie settings
              </button>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}
